import { Settings } from '@streamr/tv-engine';
import { proxy, transfer } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import { isIOS } from 'react-device-detect';
import { Env } from '../../Env';
import { canonicalUserId } from '../../utils/canonicalUserId';
import { orientationTypeToRotationMap } from '../../utils/getIosFrameRotation';
import { MediaPacketizer } from '../mediaproducer/MediaPacketizer';
import { AudioEngine } from './AudioEngine';
import { createVideoEngine } from './createVideoEngine';
import { logGapDiagnostic } from './GapDiagnostics';
import { MetricsManager } from './MetricsManager';
import { OptimalDelayMapper } from './OptimalDelayMapper';
import { PlaceholderTrack } from './PlaceholderTrack';
import { RemoteSpeechTracker } from './RemoteSpeechTracker';
import { RtmpPublishManager } from './rtmp/RtmpPublishManager';
import { SpeechDetector } from './SpeechDetector';
import { Timekeeper } from './Timekeeper';
import { encodedAudioFrameToAudioFrame } from './types/EncodedAudioFrame';
import { createUserSessionId } from './types/UserSessionId';
export class MediaManager extends EventEmitter {
    videoEngine;
    audioEngine;
    optimalDelayMapper;
    audioMuted = true;
    userMuted = false;
    videoProperties;
    networkingWorkerProxy;
    audioContext;
    contentStopExemptTrackIds = new Set();
    audioDrivenVideoTrackIds = new Set();
    avSyncLogCounter = 0;
    initCompleted = false;
    displayCanvas;
    // Track the current video track to properly clean up when a new one is added
    // This is needed for Safari where video muting requires recreating the stream
    currentVideoTrack;
    currentAudioTrack;
    speechDetector;
    speechDetectionEnabled = true;
    remoteSpeechTracker = new RemoteSpeechTracker();
    additionalVideoTracks = new Map();
    additionalAudioTracks = new Map();
    rtmpPublishManager;
    // Viewer composite output state
    viewerCompositeVideoTrack;
    viewerCompositeActive = false;
    lastOutputtedVideoFrameNumber = 0n;
    lastOutputtedAudioFrameNumber = 0n;
    lastEmittedVideoTrackCount = 0;
    lastEmittedAudioTrackCount = 0;
    // Mixed audio capture refcount (viewer + RTMP share the extractor)
    mixedAudioCaptureRefCount = 0;
    // Stored thumbnail config (with original userIds) for re-resolution when tracks change
    pendingThumbnailEntries = [];
    orientationChangeHandler;
    activePublishers = new Set();
    localUserId;
    audioTrackStates = new Map();
    placeholderTracks = new Map();
    shownPlaceholderTracks = new Set();
    suppressPlaceholderEmit = false;
    placeholderSyncDirty = false;
    ografTracks = new Map();
    // N-1 audio state: per-guest publishing (excludes guest's own audio from the mix)
    inboundAudioTrackToUser = new Map();
    n1PublishState = new Map();
    constructor() {
        super();
        this.optimalDelayMapper = new OptimalDelayMapper();
        this.audioContext = new AudioContext({
            sampleRate: Settings.audioSampleRate,
        });
        // Bypass iOS silent switch: route Web Audio through the media channel
        const nav = navigator;
        if (nav.audioSession) {
            nav.audioSession.type = 'playback';
        }
        this.videoEngine = createVideoEngine();
        this.audioEngine = new AudioEngine(this.audioContext);
        this.audioContext.addEventListener('statechange', () => {
            if (this.audioContext.state === 'running' && !this.userMuted) {
                this.emit('audioContextBlocked', false);
            }
        });
        queueMicrotask(() => {
            if (this.audioContext.state === 'suspended') {
                this.emit('audioContextBlocked', true);
            }
        });
        this.remoteSpeechTracker.on('dominantSpeakerChanged', (userSessionId, userId) => {
            this.emit('dominantSpeakerChanged', userSessionId, userId);
        });
        this.remoteSpeechTracker.on('remoteSpeechStateChanged', (userSessionId, userId, isSpeaking, audioLevel) => {
            this.emit('remoteSpeechStateChanged', userSessionId, userId, isSpeaking, audioLevel);
        });
    }
    async init(networkingWorkerProxy) {
        console.log('MediaManager::start()');
        this.emit('videoPropertiesChanged', {
            width: Settings.videoEncoderDefaultWidth,
            height: Settings.videoEncoderDefaultHeight,
            type: 'camera',
            rotation: 0,
        });
        this.networkingWorkerProxy = networkingWorkerProxy;
        await this.videoEngine.init(async (trackId, optimalDelay) => {
            this.optimalDelayMapper.updateOptimalDelay(trackId, optimalDelay);
            const sessionId = this.optimalDelayMapper.getUserSessionIdForTrack(trackId);
            if (sessionId) {
                for (const tid of this.optimalDelayMapper.getTrackIdsForSession(sessionId)) {
                    if (this.optimalDelayMapper.getTrackType(tid) ===
                        'audio') {
                        this.audioEngine.updateDecoderTrackOptimalDelay(tid);
                    }
                }
            }
        }, async (_encodedVideoOutputTrack) => {
            // In the new architecture, encoded video frames flow directly
            // from VideoEncoderWorker → NetworkingWorker via MessagePort.
            // No main-thread reading needed.
        });
        const INITIAL_AUDIO_BUFFER_MS = 80;
        await this.audioEngine.init((trackId) => {
            return (this.optimalDelayMapper.getOptimalDelayForSession(trackId) ?? INITIAL_AUDIO_BUFFER_MS);
        }, async (_encodedAudioOutputTrack) => {
            // In the new architecture, encoded audio frames flow directly
            // from AudioEncoderWorker → NetworkingWorker via MessagePort.
            // No main-thread reading needed.
        });
        this.audioEngine.referenceTimeEmitter.on('newReferenceTimes', (audioTrackId, presentationTimeMicros, realTimeMillis) => {
            const videoTrackIds = this.resolveVideoTrackIdsForAudio(audioTrackId);
            if (videoTrackIds.length > 0) {
                const newIds = videoTrackIds.filter((id) => !this.audioDrivenVideoTrackIds.has(id));
                if (newIds.length > 0) {
                    for (const id of newIds) {
                        this.audioDrivenVideoTrackIds.add(id);
                    }
                    this.videoEngine
                        .setAudioDriven(newIds, true)
                        .catch(console.error);
                }
                this.videoEngine
                    .setReferenceTimes(videoTrackIds, presentationTimeMicros, realTimeMillis)
                    .catch(console.error);
                this.logAudioVsVideoOffset(videoTrackIds, presentationTimeMicros);
            }
        });
        this.audioEngine.referenceTimeEmitter.on('audioOptimalDelay', (audioTrackId, optimalDelay) => {
            this.optimalDelayMapper.updateOptimalDelay(audioTrackId, optimalDelay);
            const sessionId = this.optimalDelayMapper.getUserSessionIdForTrack(audioTrackId);
            if (sessionId) {
                for (const tid of this.optimalDelayMapper.getTrackIdsForSession(sessionId)) {
                    if (this.optimalDelayMapper.getTrackType(tid) ===
                        'audio') {
                        this.audioEngine.updateDecoderTrackOptimalDelay(tid);
                    }
                }
            }
        });
        this.audioEngine.referenceTimeEmitter.on('audioUnderflow', (audioTrackId) => {
            const videoTrackIds = this.resolveVideoTrackIdsForAudio(audioTrackId);
            if (videoTrackIds.length > 0) {
                this.videoEngine
                    .reportUnderflow(videoTrackIds)
                    .catch(console.error);
            }
        });
        this.audioEngine.referenceTimeEmitter.on('playbackStarted', (trackId) => {
            if (this.contentStopExemptTrackIds.has(trackId)) {
                this.emit('oneToOneAudioReady');
            }
            else {
                this.emit('contentAudioReady');
            }
        });
        this.initCompleted = true;
        if (this.displayCanvas) {
            await this.videoEngine.setDisplayCanvas(this.displayCanvas);
        }
    }
    /**
     * Resolve video track IDs for an audio track. Tries same-session first;
     * falls back to same-user lookup so that 1:1 N-1 audio (which may be in
     * a different session than the content video) can still steer the video
     * playback clock.
     */
    resolveVideoTrackIdsForAudio(audioTrackId) {
        const sameSession = this.optimalDelayMapper.getVideoTrackIdsInSameSession(audioTrackId);
        if (sameSession.length > 0) {
            return sameSession;
        }
        const userId = this.optimalDelayMapper.getUserIdForTrack(audioTrackId);
        if (!userId) {
            return [];
        }
        const result = this.optimalDelayMapper.getVideoTrackIdsForUser(userId);
        return result;
    }
    logAudioVsVideoOffset(videoTrackIds, audioPresentationTimeMicros) {
        this.avSyncLogCounter++;
        if (this.avSyncLogCounter % 150 !== 1) {
            return;
        }
        const audioPtsMs = audioPresentationTimeMicros / 1000;
        this.videoEngine
            .getClockDiagnostics(videoTrackIds)
            .then((diag) => {
            for (const d of diag) {
                if (d.currentFrameMs != null) {
                    MetricsManager.getInstance().logMetric('audioVsVideoOffsetMs', Math.round(audioPtsMs - d.currentFrameMs));
                }
            }
        })
            .catch(() => { });
    }
    async wireNetworkingPorts(opts) {
        if (!this.networkingWorkerProxy) {
            return;
        }
        if (!opts?.skipIndividualPorts &&
            this.videoEngine.isVideoEncoderWorkerRunning()) {
            const ch = new MessageChannel();
            await this.videoEngine.connectEncoderNetworkingPort?.(ch.port1);
            await this.networkingWorkerProxy.connectVideoEncoderPort(transfer(ch.port2, [ch.port2]));
        }
        if (!opts?.skipIndividualPorts &&
            this.audioEngine.isAudioEncoderWorkerRunning()) {
            const ch = new MessageChannel();
            await this.audioEngine.connectEncoderNetworkingPort(ch.port1);
            await this.networkingWorkerProxy.connectAudioEncoderPort(transfer(ch.port2, [ch.port2]));
        }
        if (this.videoEngine.isVideoDecoderWorkerRunning()) {
            const ch = new MessageChannel();
            await this.videoEngine.connectDecoderNetworkingPort?.(ch.port1);
            await this.networkingWorkerProxy.connectVideoDecoderPort(transfer(ch.port2, [ch.port2]));
        }
        if (!this.audioEngine.isAudioDecoderWorkerRunning()) {
            await this.audioEngine.startAudioDecoderWorker();
        }
        if (this.audioEngine.isAudioDecoderWorkerRunning()) {
            const ch = new MessageChannel();
            await this.audioEngine.connectDecoderNetworkingPort(ch.port1);
            await this.networkingWorkerProxy.connectAudioDecoderPort(transfer(ch.port2, [ch.port2]));
        }
    }
    // ========== Inbound track lifecycle (called by StreamrAppApi from NetworkingWorker events) ==========
    handleNewInboundVideoTrack(trackId, userSessionId, userId) {
        const uid = canonicalUserId(userId);
        const placeholderWasShown = this.shownPlaceholderTracks.has(uid);
        this.optimalDelayMapper.registerTrackIfNotExists(trackId, userSessionId, 'video');
        if (placeholderWasShown) {
            this.hidePlaceholderTrack(uid);
        }
        if (this.lowEndFocusUserId) {
            this.resolveLowEndFocusTrack();
        }
        this.activePublishers.add(uid);
        this.emitActiveVideoTracks();
        this.emit('activePublishersChanged', Array.from(this.activePublishers));
    }
    handleInboundVideoTrackDone(trackId) {
        this.optimalDelayMapper.removeTrack(trackId);
        this.audioDrivenVideoTrackIds.delete(trackId);
        this.emitActiveVideoTracks();
    }
    handleNewInboundAudioTrack(trackId, userSessionId, userId) {
        const uid = canonicalUserId(userId);
        this.inboundAudioTrackToUser.set(trackId, uid);
        this.optimalDelayMapper.registerTrackIfNotExists(trackId, userSessionId, 'audio');
        const existingDelay = this.optimalDelayMapper.getOptimalDelayForSession(trackId);
        if (existingDelay !== undefined) {
            this.audioEngine.updateDecoderTrackOptimalDelay(trackId);
        }
        this.emitActiveAudioTracks();
        this.activePublishers.add(uid);
        this.emit('activePublishersChanged', Array.from(this.activePublishers));
    }
    handleInboundAudioTrackDone(trackId) {
        this.inboundAudioTrackToUser.delete(trackId);
        this.optimalDelayMapper.removeTrack(trackId);
        this.audioTrackStates.delete(trackId);
        this.emitActiveAudioTracks();
    }
    handleRemoteSpeechState(userSessionId, userId, audioLevel, isSpeaking) {
        this.remoteSpeechTracker.update(userSessionId, userId, audioLevel, isSpeaking);
    }
    handleNetworkLeave(userId, messageChainId) {
        const uid = canonicalUserId(userId);
        const userSessionId = createUserSessionId(userId, messageChainId);
        console.log('[MediaManager] handleNetworkLeave', {
            userId: uid,
            messageChainId,
            userSessionId,
        });
        this.optimalDelayMapper.removeSession(userSessionId);
        this.remoteSpeechTracker.removeSession(userSessionId);
        this.emitActiveVideoTracks();
        this.videoEngine.handleLeave(userSessionId).catch(console.error);
        this.audioEngine.handleLeave(userSessionId).catch(console.error);
        if (this.activePublishers.delete(uid)) {
            this.emit('activePublishersChanged', Array.from(this.activePublishers));
        }
    }
    handleStaleSessionCleaned(userSessionId, staleTracks) {
        console.log('[MediaManager] handleStaleSessionCleaned', {
            userSessionId,
            staleTracks: staleTracks.map((t) => t.slice(0, 8)),
            n1StateSize: this.n1PublishState.size,
        });
        this.optimalDelayMapper.removeSession(userSessionId);
        this.remoteSpeechTracker.removeSession(userSessionId);
        this.videoEngine.handleLeave(userSessionId).catch(console.error);
        this.audioEngine.handleLeave(userSessionId).catch(console.error);
        this.emitActiveVideoTracks();
        // Clean up stale audio track mappings and restart N-1 if affected.
        // When a guest refreshes, the old trackId becomes stale but may still
        // be used as the N-1 exclusion key, causing the guest to hear themselves.
        const affectedUsers = new Set();
        for (const trackId of staleTracks) {
            const uid = this.inboundAudioTrackToUser.get(trackId);
            if (uid) {
                this.inboundAudioTrackToUser.delete(trackId);
                affectedUsers.add(uid);
            }
        }
        for (const uid of affectedUsers) {
            const n1State = this.n1PublishState.get(uid);
            if (n1State) {
                console.log(`[MediaManager] Stale session invalidated N-1 for ${uid}, stopping so it can be restarted with the new track`, { oldTrackId: n1State.audioTrackId });
                this.invalidateN1ForGuest(uid).catch(console.error);
            }
            else {
                console.log(`[MediaManager] Stale session for ${uid}, no active N-1 to invalidate — awaiting new audio track`);
            }
        }
    }
    async setDisplayCanvas(canvas) {
        this.displayCanvas = canvas;
        if (this.initCompleted) {
            await this.videoEngine.setDisplayCanvas(canvas);
        }
    }
    async setLiveSceneThumbnailCanvasMounted(canvas) {
        if (this.initCompleted) {
            await this.videoEngine.setLiveSceneThumbnailCanvasMounted(canvas);
        }
    }
    async captureDisplayFrame(width, height) {
        if (!this.initCompleted)
            return '';
        return this.videoEngine.captureDisplayFrame(width, height);
    }
    isAudioMuted() {
        return this.audioMuted;
    }
    getVideoProperties() {
        return this.videoProperties;
    }
    currentCanvasRotation = 0;
    updateVideoPropertiesForTrack(track) {
        const settings = track.getSettings();
        const isScreenSharing = settings.displaySurface !== undefined &&
            settings.displaySurface !== '';
        const newProps = {
            width: Settings.videoEncoderDefaultWidth,
            height: Settings.videoEncoderDefaultHeight,
            type: isScreenSharing ? 'screen' : 'camera',
            rotation: this.currentCanvasRotation,
        };
        if (!this.videoProperties ||
            this.videoProperties.width !== newProps.width ||
            this.videoProperties.height !== newProps.height ||
            this.videoProperties.rotation !== newProps.rotation ||
            this.videoProperties.type !== newProps.type) {
            this.videoProperties = newProps;
            this.emit('videoPropertiesChanged', newProps);
        }
    }
    setupOrientationListener() {
        this.removeOrientationListener();
        if (!isIOS) {
            return;
        }
        void this.sendCurrentOrientationToEngine();
        this.orientationChangeHandler = () => {
            void this.sendCurrentOrientationToEngine().then(() => {
                if (this.currentVideoTrack) {
                    this.updateVideoPropertiesForTrack(this.currentVideoTrack);
                }
            });
        };
        screen.orientation.addEventListener('change', this.orientationChangeHandler);
    }
    async sendCurrentOrientationToEngine() {
        this.currentCanvasRotation =
            orientationTypeToRotationMap[screen.orientation.type] ?? 0;
        await this.videoEngine
            .setScreenOrientation(screen.orientation.type, screen.orientation.angle)
            .catch((err) => {
            console.warn('[MediaManager] setScreenOrientation failed', err);
        });
    }
    removeOrientationListener() {
        if (this.orientationChangeHandler) {
            screen.orientation.removeEventListener('change', this.orientationChangeHandler);
            this.orientationChangeHandler = undefined;
        }
    }
    async addVideoTrack(videoTrack) {
        console.log('MediaManager::addVideoTrack', videoTrack);
        if (!videoTrack) {
            if (this.currentVideoTrack) {
                console.log('MediaManager::addVideoTrack removing track (video muted)', {
                    trackId: this.currentVideoTrack.id,
                });
                await this.videoEngine.removeMediaStreamVideoTrack(this.currentVideoTrack);
                this.currentVideoTrack = undefined;
            }
            if (this.localUserId) {
                this.showPlaceholderTrack(this.localUserId);
            }
            this.emitActiveVideoTracks();
            return;
        }
        if (videoTrack.kind !== 'video') {
            throw new Error('videoTrack is not a video track');
        }
        // If the same track is being added, skip to avoid duplicates
        if (this.currentVideoTrack?.id === videoTrack.id) {
            console.log('MediaManager::addVideoTrack skipping duplicate track', {
                trackId: videoTrack.id,
            });
            return;
        }
        // Remove the previous track before adding a new one
        // This is needed for Safari where video muting requires recreating the stream
        if (this.currentVideoTrack) {
            console.log('MediaManager::addVideoTrack removing previous track', {
                previousTrackId: this.currentVideoTrack.id,
                newTrackId: videoTrack.id,
            });
            await this.videoEngine.removeMediaStreamVideoTrack(this.currentVideoTrack);
        }
        this.currentVideoTrack = videoTrack;
        if (this.localUserId) {
            this.hidePlaceholderTrack(this.localUserId);
        }
        this.emitActiveVideoTracks();
        console.log('MediaManager::addVideoTrack - NEW TRACK', {
            trackId: videoTrack.id.slice(0, 8),
            displaySurface: videoTrack.getSettings().displaySurface,
        });
        await this.videoEngine.addMediaStreamVideoTrack(videoTrack);
        this.setupOrientationListener();
        this.updateVideoPropertiesForTrack(videoTrack);
        await this.resyncThumbnailConfig();
    }
    setPerTrackVideoBitrate(trackId, bitrate) {
        this.videoEngine.setPerTrackVideoBitrate(trackId, bitrate);
    }
    async addAudioTrack(audioTrack) {
        if (!audioTrack) {
            return;
        }
        if (audioTrack && audioTrack.kind !== 'audio') {
            throw new Error('audioTrack is not a audio track');
        }
        console.log('[MediaManager] addAudioTrack', {
            trackId: audioTrack.id.slice(0, 8),
        });
        this.destroySpeechDetector();
        this.currentAudioTrack = audioTrack;
        if (this.speechDetectionEnabled) {
            this.speechDetector = new SpeechDetector(this.audioContext, audioTrack);
            this.speechDetector.on('speechStateChanged', (isSpeaking) => {
                const level = this.speechDetector?.getAudioLevel() ?? 0;
                this.networkingWorkerProxy?.setSpeechState(isSpeaking, level);
                this.emit('speechStateChanged', isSpeaking, level);
            });
            this.speechDetector.on('audioLevelChanged', (level) => {
                const isSpeaking = this.speechDetector?.getIsSpeaking() ?? false;
                this.networkingWorkerProxy?.setSpeechState(isSpeaking, level);
            });
        }
        await this.audioEngine.addMediaStreamAudioTrack(audioTrack);
        this.emitActiveAudioTracks();
    }
    async addAdditionalVideoTrack(track) {
        if (track.kind !== 'video') {
            throw new Error('track is not a video track');
        }
        if (this.additionalVideoTracks.has(track.id) ||
            this.currentVideoTrack?.id === track.id) {
            console.log('MediaManager::addAdditionalVideoTrack skipping duplicate', { trackId: track.id });
            return;
        }
        console.log('MediaManager::addAdditionalVideoTrack', {
            trackId: track.id.slice(0, 8),
        });
        this.additionalVideoTracks.set(track.id, track);
        await this.videoEngine.addMediaStreamVideoTrack(track);
        this.emitActiveVideoTracks();
        await this.resyncThumbnailConfig();
    }
    async addAdditionalAudioTrack(track) {
        if (track.kind !== 'audio') {
            throw new Error('track is not an audio track');
        }
        if (this.additionalAudioTracks.has(track.id) ||
            this.currentAudioTrack?.id === track.id) {
            console.log('MediaManager::addAdditionalAudioTrack skipping duplicate', { trackId: track.id });
            return;
        }
        console.log('MediaManager::addAdditionalAudioTrack', {
            trackId: track.id.slice(0, 8),
        });
        this.additionalAudioTracks.set(track.id, track);
        await this.audioEngine.addMediaStreamAudioTrack(track);
        this.emitActiveAudioTracks();
    }
    async removeLocalVideoTrack(trackId) {
        let track;
        if (this.currentVideoTrack?.id === trackId) {
            track = this.currentVideoTrack;
            this.currentVideoTrack = undefined;
        }
        else {
            track = this.additionalVideoTracks.get(trackId);
            this.additionalVideoTracks.delete(trackId);
        }
        if (track) {
            console.log('MediaManager::removeLocalVideoTrack', {
                trackId: trackId.slice(0, 8),
            });
            await this.videoEngine.removeMediaStreamVideoTrack(track);
            if (this.localUserId &&
                !this.currentVideoTrack &&
                this.additionalVideoTracks.size === 0) {
                this.showPlaceholderTrack(this.localUserId);
            }
            this.emitActiveVideoTracks();
            await this.resyncThumbnailConfig();
        }
    }
    async removeLocalAudioTrack(trackId) {
        let track;
        if (this.currentAudioTrack?.id === trackId) {
            track = this.currentAudioTrack;
            this.currentAudioTrack = undefined;
            this.destroySpeechDetector();
        }
        else {
            track = this.additionalAudioTracks.get(trackId);
            this.additionalAudioTracks.delete(trackId);
        }
        if (track) {
            console.log('MediaManager::removeLocalAudioTrack', {
                trackId: trackId.slice(0, 8),
            });
            await this.audioEngine.removeMediaStreamAudioTrack(track);
            this.audioTrackStates.delete(trackId);
            this.emitActiveAudioTracks();
        }
    }
    async removeAllLocalTracks() {
        const videoTrackIds = [];
        if (this.currentVideoTrack) {
            videoTrackIds.push(this.currentVideoTrack.id);
        }
        for (const id of this.additionalVideoTracks.keys()) {
            videoTrackIds.push(id);
        }
        const audioTrackIds = [];
        if (this.currentAudioTrack) {
            audioTrackIds.push(this.currentAudioTrack.id);
        }
        for (const id of this.additionalAudioTracks.keys()) {
            audioTrackIds.push(id);
        }
        for (const id of videoTrackIds) {
            await this.removeLocalVideoTrack(id);
        }
        for (const id of audioTrackIds) {
            await this.removeLocalAudioTrack(id);
        }
    }
    getActivePublishers() {
        return Array.from(this.activePublishers);
    }
    handleUserOffline(userId) {
        const uid = canonicalUserId(userId);
        this.stopN1PublishingForGuest(uid).catch(console.error);
        const sessions = this.optimalDelayMapper.getSessionsForUser(uid);
        for (const userSessionId of sessions) {
            console.log('[MediaManager] cleaning up offline user session', {
                userId,
                userSessionId,
            });
            this.optimalDelayMapper.removeSession(userSessionId);
            this.remoteSpeechTracker.removeSession(userSessionId);
            this.videoEngine.handleLeave(userSessionId).catch(console.error);
            this.audioEngine.handleLeave(userSessionId).catch(console.error);
        }
        if (sessions.length > 0) {
            this.emitActiveAudioTracks();
            this.emitActiveVideoTracks();
        }
        if (this.activePublishers.delete(uid)) {
            this.emit('activePublishersChanged', Array.from(this.activePublishers));
        }
    }
    createPlaceholderTrack(userId, displayName, isLocal = false) {
        const uid = canonicalUserId(userId);
        if (this.placeholderTracks.has(uid))
            return;
        const trackId = `placeholder:${uid}`;
        const track = new PlaceholderTrack(trackId, displayName, uid, isLocal);
        this.placeholderTracks.set(uid, track);
        console.log('MediaManager::createPlaceholderTrack', {
            userId: uid.slice(0, 8),
            trackId: trackId.slice(0, 20),
            isLocal,
        });
    }
    destroyPlaceholderTrack(userId) {
        const uid = canonicalUserId(userId);
        const track = this.placeholderTracks.get(uid);
        if (!track)
            return;
        track.destroy();
        this.placeholderTracks.delete(uid);
        const wasShown = this.shownPlaceholderTracks.delete(uid);
        if (wasShown) {
            this.emitActiveVideoTracks();
        }
        console.log('MediaManager::destroyPlaceholderTrack', {
            userId: uid.slice(0, 8),
            wasShown,
        });
    }
    showPlaceholderTrack(userId) {
        const uid = canonicalUserId(userId);
        const track = this.placeholderTracks.get(uid);
        const alreadyShown = this.shownPlaceholderTracks.has(uid);
        if (!track || alreadyShown)
            return;
        this.shownPlaceholderTracks.add(uid);
        track.clearPendingHide();
        this.videoEngine.addVideoTrack(track).catch(console.error);
        if (this.suppressPlaceholderEmit) {
            this.placeholderSyncDirty = true;
        }
        else {
            this.emitActiveVideoTracks();
        }
        console.log('MediaManager::showPlaceholderTrack', {
            userId: uid.slice(0, 8),
        });
    }
    hidePlaceholderTrack(userId) {
        const uid = canonicalUserId(userId);
        const track = this.placeholderTracks.get(uid);
        if (!track || !this.shownPlaceholderTracks.has(uid))
            return;
        track.hide();
        this.shownPlaceholderTracks.delete(uid);
        if (this.suppressPlaceholderEmit) {
            this.placeholderSyncDirty = true;
        }
        else {
            this.emitActiveVideoTracks();
        }
        console.log('MediaManager::hidePlaceholderTrack', {
            userId: uid.slice(0, 8),
        });
    }
    updatePlaceholderTrack(userId, displayName) {
        const uid = canonicalUserId(userId);
        const track = this.placeholderTracks.get(uid);
        if (track) {
            track.updateDisplayName(displayName);
        }
    }
    beginPlaceholderSync() {
        this.suppressPlaceholderEmit = true;
        this.placeholderSyncDirty = false;
    }
    endPlaceholderSync() {
        this.suppressPlaceholderEmit = false;
        if (this.placeholderSyncDirty) {
            this.placeholderSyncDirty = false;
            this.emitActiveVideoTracks();
        }
    }
    hasRealVideoTracksForUser(userId) {
        const trackIds = this.optimalDelayMapper.getVideoTrackIdsForUser(userId);
        return trackIds.length > 0;
    }
    addOgrafTrack(track) {
        void track.getTrackId().then((trackId) => {
            if (this.ografTracks.has(trackId))
                return;
            this.ografTracks.set(trackId, track);
            this.videoEngine.addVideoTrack(track).catch(console.error);
            this.emitActiveVideoTracks();
        });
    }
    removeOgrafTrack(trackId) {
        const track = this.ografTracks.get(trackId);
        if (!track)
            return;
        track.hide();
        this.ografTracks.delete(trackId);
        this.emitActiveVideoTracks();
    }
    async stop() {
        this.removeOrientationListener();
        await this.stopAllN1Publishing();
        await this.removeAllRtmpDestinations();
        for (const [, track] of this.ografTracks) {
            track.destroy();
        }
        this.ografTracks.clear();
        for (const [, track] of this.placeholderTracks) {
            track.destroy();
        }
        this.placeholderTracks.clear();
        this.shownPlaceholderTracks.clear();
        await this.videoEngine.destroy();
        await this.audioEngine.destroy();
        this.destroySpeechDetector();
        this.remoteSpeechTracker.destroy();
        this.activePublishers.clear();
        this.lastOutputtedVideoFrameNumber = 0n;
        this.lastOutputtedAudioFrameNumber = 0n;
        this.currentVideoTrack = undefined;
        this.currentAudioTrack = undefined;
        this.additionalVideoTracks.clear();
        this.additionalAudioTracks.clear();
        this.networkingWorkerProxy = undefined;
    }
    getRemoteSpeechTracker() {
        return this.remoteSpeechTracker;
    }
    destroySpeechDetector() {
        if (this.speechDetector) {
            this.speechDetector.destroy();
            this.speechDetector = undefined;
        }
    }
    setSpeechDetectionEnabled(enabled) {
        this.speechDetectionEnabled = enabled;
        if (!enabled) {
            this.destroySpeechDetector();
        }
    }
    // ========== Worker lifecycle (delegated to engines) ==========
    async startVideoPlayerWorker() {
        await this.videoEngine.startVideoPlayerWorker();
    }
    async stopVideoPlayerWorker() {
        await this.videoEngine.stopVideoPlayerWorker();
    }
    async startVideoDecoderWorker() {
        await this.videoEngine.startVideoDecoderWorker();
    }
    async stopVideoDecoderWorker() {
        await this.videoEngine.stopVideoDecoderWorker();
    }
    async enableDirectVideoRendering() {
        return this.videoEngine.enableDirectVideoRendering();
    }
    async enableDirectVideoOutput() {
        return this.videoEngine.enableDirectVideoOutput();
    }
    async disableDirectVideoOutput() {
        await this.videoEngine.disableDirectVideoOutput();
    }
    async startVideoEncoderWorker() {
        await this.videoEngine.startVideoEncoderWorker();
    }
    async stopVideoEncoderWorker() {
        await this.videoEngine.stopVideoEncoderWorker();
    }
    async startAudioEncoderWorker() {
        await this.audioEngine.startAudioEncoderWorker();
    }
    async stopAudioEncoderWorker() {
        await this.audioEngine.stopAudioEncoderWorker();
    }
    isVideoPlayerWorkerRunning() {
        return this.videoEngine.isVideoPlayerWorkerRunning();
    }
    isVideoDecoderWorkerRunning() {
        return this.videoEngine.isVideoDecoderWorkerRunning();
    }
    isVideoEncoderWorkerRunning() {
        return this.videoEngine.isVideoEncoderWorkerRunning();
    }
    isAudioEncoderWorkerRunning() {
        return this.audioEngine.isAudioEncoderWorkerRunning();
    }
    setPerTrackAudioEncodingEnabled(enabled) {
        this.audioEngine.setPerTrackEncodingEnabled(enabled);
    }
    setPerTrackVideoEncodingEnabled(enabled) {
        this.videoEngine.setPerTrackVideoEncodingEnabled?.(enabled);
    }
    async startEncoderWorkers() {
        await Promise.all([
            this.startVideoEncoderWorker(),
            this.startAudioEncoderWorker(),
        ]);
    }
    async stopEncoderWorkers() {
        await Promise.all([
            this.stopVideoEncoderWorker(),
            this.stopAudioEncoderWorker(),
        ]);
    }
    setVideoActive(active) {
        this.networkingWorkerProxy?.setIsVideoActive(active);
    }
    async unmuteAudio() {
        console.log('MediaManager::unmuteAudio() called');
        await this.audioEngine.resumeAllContexts();
        this.audioMuted = false;
        this.userMuted = false;
        this.emit('muteChange', false);
        this.emit('userMutedChange', false);
        this.emit('audioContextBlocked', this.audioContext.state !== 'running');
    }
    async stopContentAudio() {
        const removedTrackIds = this.audioEngine.stopContentAudioTracks();
        for (const trackId of removedTrackIds) {
            await this.audioEngine.destroyDecoderTrack(trackId);
            this.optimalDelayMapper.removeTrack(trackId);
        }
        await this.networkingWorkerProxy?.setContentAudioBlocked(true);
        console.log(`MediaManager::stopContentAudio() removed ${removedTrackIds.length} tracks`);
    }
    async resumeContentAudio() {
        await this.networkingWorkerProxy?.setContentAudioBlocked(false);
        this.audioEngine.resumeContentAudioTracks();
        this.contentStopExemptTrackIds.clear();
        console.log('MediaManager::resumeContentAudio()');
    }
    exemptTrackFromContentStop(trackId) {
        this.contentStopExemptTrackIds.add(trackId);
        this.audioEngine.exemptTrackFromContentStop(trackId);
    }
    async muteAudio() {
        console.log('MediaManager::muteAudio() called');
        await this.audioEngine.suspendAllContexts();
        if (this.audioDrivenVideoTrackIds.size > 0) {
            const trackIds = [...this.audioDrivenVideoTrackIds];
            this.audioDrivenVideoTrackIds.clear();
            this.videoEngine
                .setAudioDriven(trackIds, false)
                .catch(console.error);
        }
        this.audioMuted = true;
        this.userMuted = true;
        this.emit('muteChange', true);
        this.emit('userMutedChange', true);
        this.emit('audioContextBlocked', false);
    }
    async acquireMixedAudioCapture() {
        if (this.mixedAudioCaptureRefCount === 0) {
            await this.audioEngine.startMixedAudioCapture();
        }
        this.mixedAudioCaptureRefCount++;
        console.log(`[MediaManager] acquireMixedAudioCapture refCount=${this.mixedAudioCaptureRefCount}`);
    }
    async releaseMixedAudioCapture() {
        this.mixedAudioCaptureRefCount--;
        console.log(`[MediaManager] releaseMixedAudioCapture refCount=${this.mixedAudioCaptureRefCount}`);
        if (this.mixedAudioCaptureRefCount <= 0) {
            this.mixedAudioCaptureRefCount = 0;
            await this.audioEngine.stopMixedAudioCapture();
        }
    }
    async startPublishingViewerStream(opts) {
        console.log('MediaManager::startPublishingViewerStream() called', {
            useDirectTracks: opts?.useDirectTracks ?? false,
        });
        if (opts?.useDirectTracks) {
            this.networkingWorkerProxy?.setDirectTrackPublishing(true);
            console.log('MediaManager::startPublishingViewerStream() direct track mode enabled');
            return;
        }
        const compositeMediaStreamId = crypto.randomUUID();
        this.networkingWorkerProxy?.setCompositeMediaStreamId(compositeMediaStreamId);
        // Start viewer composite video (HEVC)
        const timestampOffsetMicros = Timekeeper.getInstance('producer').getLastTimestampMicros();
        this.viewerCompositeVideoTrack =
            await this.videoEngine.startRenderedFrameCapture(timestampOffsetMicros);
        this.viewerCompositeActive = true;
        // Wire composite video encoder → NetworkingWorker via MessagePort
        if (this.networkingWorkerProxy) {
            const videoCh = new MessageChannel();
            await this.videoEngine.connectCompositeNetworkingPort?.(videoCh.port1);
            await this.networkingWorkerProxy.connectCompositeVideoEncoderPort(transfer(videoCh.port2, [videoCh.port2]));
        }
        // Start viewer composite audio (Opus) and wire to NetworkingWorker
        await this.acquireMixedAudioCapture();
        if (this.networkingWorkerProxy) {
            const audioCh = new MessageChannel();
            await this.audioEngine.connectCompositeNetworkingPort(audioCh.port1);
            await this.networkingWorkerProxy.connectCompositeAudioEncoderPort(transfer(audioCh.port2, [audioCh.port2]));
        }
        await this.audioEngine.startCompositeEncoder('viewer', Settings.viewerStreamAudioCodec, Settings.viewerStreamAudioBitrate, Settings.viewerStreamAudioChannels, async (_encodedAudioTrack) => {
            // Encoded audio now flows via MessagePort to NetworkingWorker;
            // no main-thread listener needed.
        });
        console.log('MediaManager::startPublishingViewerStream() completed');
    }
    async stopPublishingViewerStream() {
        console.log('MediaManager::stopPublishingViewerStream() called');
        this.networkingWorkerProxy?.setDirectTrackPublishing(false);
        if (this.viewerCompositeActive) {
            await this.videoEngine.stopRenderedFrameCapture();
            await this.audioEngine.stopCompositeEncoder('viewer');
            await this.releaseMixedAudioCapture();
            this.viewerCompositeVideoTrack = undefined;
            this.viewerCompositeActive = false;
        }
        console.log('MediaManager::stopPublishingViewerStream() completed');
    }
    // ========== N-1 audio publishing (per-guest) ==========
    getAudioTrackIdForUser(userId) {
        const uid = canonicalUserId(userId);
        for (const [trackId, user] of this.inboundAudioTrackToUser) {
            if (user === uid)
                return trackId;
        }
        return undefined;
    }
    async startN1PublishingForGuest(guestUserId, oneToOneStreamId) {
        const audioTrackId = this.getAudioTrackIdForUser(guestUserId);
        if (!audioTrackId) {
            console.warn(`[MediaManager] No inbound audio track found for ${guestUserId}`);
            return;
        }
        const existingState = this.n1PublishState.get(guestUserId);
        if (existingState) {
            if (existingState.audioTrackId === audioTrackId) {
                return;
            }
            // Guest's audio track changed (e.g. browser refresh) — restart
            // N-1 with the new exclusion key so the guest doesn't hear themselves.
            console.log(`[MediaManager] N-1 track changed for ${guestUserId}: ${existingState.audioTrackId} → ${audioTrackId}, restarting`);
            await this.stopN1PublishingForGuest(guestUserId);
        }
        await this.acquireMixedAudioCapture();
        const mediaStreamId = crypto.randomUUID();
        const packetizer = new MediaPacketizer({
            audioCodec: Settings.viewerStreamAudioCodec,
            separateAudioPackets: true,
        });
        const state = {
            audioTrackId,
            packetizer,
            mediaStreamId,
            oneToOneStreamId,
            lastAudioTrackId: undefined,
            lastAudioFrameNumber: 0n,
            lastN1FrameArrivalMs: undefined,
            // #region agent log
            cadenceBucket: undefined,
            // #endregion
        };
        this.n1PublishState.set(guestUserId, state);
        console.log(`[MediaManager] N-1 publishing started for guest ${guestUserId}`, { audioTrackId, oneToOneStreamId: oneToOneStreamId.slice(0, 20) });
        packetizer.on('mediaPacketFragment', (fragment) => {
            this.networkingWorkerProxy?.publishToOneToOneStream(oneToOneStreamId, fragment);
        });
        await this.audioEngine.startN1Encoder(audioTrackId, Settings.viewerStreamAudioCodec, Settings.viewerStreamAudioBitrate, Settings.viewerStreamAudioChannels, async (encodedAudioTrack) => {
            encodedAudioTrack.setListener(proxy(async (encodedAudioFrame, done) => {
                if (encodedAudioFrame) {
                    const now = performance.now();
                    const deltaMs = state.lastN1FrameArrivalMs !== undefined
                        ? now - state.lastN1FrameArrivalMs
                        : undefined;
                    state.lastN1FrameArrivalMs = now;
                    // #region agent log
                    // Per-second cadence bucket for producer-side N-1 encoder rate.
                    {
                        const arrivalDelta = deltaMs === undefined ? 0 : deltaMs;
                        let bucket = state.cadenceBucket;
                        if (bucket === undefined ||
                            now - bucket.startMs >= 1000) {
                            if (bucket !== undefined) {
                                logGapDiagnostic('mediaManager.n1Bucket', {
                                    trackId: audioTrackId,
                                    userId: guestUserId,
                                    detail: {
                                        startMs: bucket.startMs,
                                        durationMs: now -
                                            bucket.startMs,
                                        frameCount: bucket.count,
                                        maxDeltaMs: bucket.maxDeltaMs,
                                        sumDeltaMs: bucket.sumDeltaMs,
                                        oneToOneStreamId,
                                    },
                                });
                            }
                            bucket = {
                                startMs: now,
                                count: 0,
                                maxDeltaMs: 0,
                                sumDeltaMs: 0,
                            };
                            state.cadenceBucket = bucket;
                        }
                        bucket.count += 1;
                        if (arrivalDelta > bucket.maxDeltaMs)
                            bucket.maxDeltaMs = arrivalDelta;
                        bucket.sumDeltaMs += arrivalDelta;
                    }
                    // Lowered threshold 60→30ms to reveal slow drift,
                    // not just spikes (20ms frame cadence).
                    // #endregion
                    if (deltaMs === undefined || deltaMs > 30) {
                        logGapDiagnostic('mediaManager.n1Encoded', {
                            trackId: audioTrackId,
                            userId: guestUserId,
                            frameNumber: encodedAudioFrame.frameNumber.toString(),
                            deltaMs,
                            detail: {
                                encodedTrackId: encodedAudioFrame.trackId,
                                oneToOneStreamId,
                                timestamp: encodedAudioFrame.timestamp.toString(),
                            },
                        });
                    }
                    state.lastAudioTrackId =
                        encodedAudioFrame.trackId;
                    state.lastAudioFrameNumber =
                        encodedAudioFrame.frameNumber;
                    const audioFrame = encodedAudioFrameToAudioFrame(encodedAudioFrame);
                    packetizer.addAudioFrames([audioFrame], mediaStreamId);
                }
                if (done && state.lastAudioTrackId) {
                    packetizer.sendDoneAudioFrame(state.lastAudioTrackId, state.lastAudioFrameNumber + 1n, mediaStreamId);
                }
            }));
        });
        console.log(`[MediaManager] N-1 publishing started for guest ${guestUserId}, excluding track ${audioTrackId.slice(0, 8)}`, { n1StateSize: this.n1PublishState.size });
    }
    async stopN1PublishingForGuest(guestUserId) {
        const state = this.n1PublishState.get(guestUserId);
        if (!state)
            return;
        await this.audioEngine.stopN1Encoder(state.audioTrackId);
        await this.releaseMixedAudioCapture();
        this.n1PublishState.delete(guestUserId);
        console.log(`[MediaManager] N-1 publishing stopped for guest ${guestUserId}`, { n1StateSize: this.n1PublishState.size });
    }
    /**
     * Stops the N-1 encoder for a guest WITHOUT releasing the shared
     * mixedAudioCapture. Used during stale-session cleanup where the
     * guest is about to reconnect and N-1 will be restarted immediately.
     * Releasing + re-acquiring mixedAudioCapture would destroy and
     * recreate the compositeAudioExtractor, breaking the viewer
     * composite audio encoder.
     */
    async invalidateN1ForGuest(guestUserId) {
        const state = this.n1PublishState.get(guestUserId);
        if (!state)
            return;
        // Delete synchronously BEFORE the await to prevent a concurrent
        // startN1PublishingForGuest from seeing the stale entry and calling
        // stopN1PublishingForGuest (which would release mixedAudioCapture).
        this.n1PublishState.delete(guestUserId);
        await this.audioEngine.stopN1Encoder(state.audioTrackId);
        console.log(`[MediaManager] N-1 invalidated (without releasing mixedAudioCapture) for guest ${guestUserId}`);
    }
    async stopAllN1Publishing() {
        for (const guestUserId of [...this.n1PublishState.keys()]) {
            await this.stopN1PublishingForGuest(guestUserId);
        }
    }
    /**
     * Add an RTMP rebroadcast destination.
     * Can be called at any time, even while already streaming.
     * @returns destination ID that can be used to remove the destination later.
     */
    rtmpCaptureActive = false;
    async addRtmpDestination(rtmpUrl, proxyWsUrl, onStatusChange) {
        const effectiveProxyUrl = proxyWsUrl ?? Env.RTMP_PROXY_URL;
        console.log('MediaManager::addRtmpDestination()', {
            rtmpUrl,
            proxyWsUrl: effectiveProxyUrl,
        });
        if (!this.rtmpPublishManager) {
            this.rtmpPublishManager = new RtmpPublishManager();
        }
        if (onStatusChange) {
            this.rtmpPublishManager.setStatusCallback(onStatusChange);
        }
        if (!this.rtmpCaptureActive) {
            // RTMP composite video (AVC) — worker-to-worker via MessagePort
            const { port1, port2 } = new MessageChannel();
            this.rtmpPublishManager.connectVideoPort(port1);
            await this.videoEngine.startRtmpCapture(port2);
            // RTMP composite audio (AAC)
            await this.acquireMixedAudioCapture();
            await this.audioEngine.startCompositeEncoder('rtmp', Settings.rtmpAudioCodec, Settings.rtmpAudioBitrate, Settings.rtmpAudioChannels, async (encodedAudioTrack) => {
                encodedAudioTrack.setListener(proxy(async (encodedAudioFrame) => {
                    if (encodedAudioFrame) {
                        this.rtmpPublishManager?.forwardAudioFrame(encodedAudioFrame);
                    }
                }));
            });
            this.rtmpCaptureActive = true;
        }
        const destinationId = await this.rtmpPublishManager.addDestination(rtmpUrl, effectiveProxyUrl);
        console.log('MediaManager::addRtmpDestination() completed', {
            destinationId,
        });
        return destinationId;
    }
    /**
     * Remove a single RTMP rebroadcast destination by ID.
     */
    async removeRtmpDestination(destinationId) {
        console.log('MediaManager::removeRtmpDestination()', { destinationId });
        await this.rtmpPublishManager?.removeDestination(destinationId);
    }
    /**
     * Remove all RTMP rebroadcast destinations and clean up the worker.
     */
    async removeAllRtmpDestinations() {
        console.log('MediaManager::removeAllRtmpDestinations()');
        if (this.rtmpCaptureActive) {
            await this.videoEngine.stopRtmpCapture();
            await this.audioEngine.stopCompositeEncoder('rtmp');
            await this.releaseMixedAudioCapture();
            this.rtmpCaptureActive = false;
        }
        await this.rtmpPublishManager?.removeAllDestinations();
        this.rtmpPublishManager = undefined;
    }
    async handleCanvasClick(x, y) {
        const trackId = await this.videoEngine.getVideoAtPosition(x, y);
        if (trackId) {
            await this.videoEngine.togglePinVideo(trackId);
        }
    }
    /**
     * If a remote user has real inbound video registered but their placeholder is
     * still marked shown (e.g. id form mismatch), remove it before composing the
     * track list. Skips the local user — local camera is not in OptimalDelayMapper.
     */
    repairStalePlaceholderTracksForRemoteVideo() {
        const localCanonical = this.localUserId
            ? canonicalUserId(this.localUserId)
            : undefined;
        for (const uid of [...this.shownPlaceholderTracks]) {
            if (localCanonical !== undefined && uid === localCanonical) {
                continue;
            }
            if (this.optimalDelayMapper.getVideoTrackIdsForUser(uid).length ===
                0) {
                continue;
            }
            const track = this.placeholderTracks.get(uid);
            if (!track)
                continue;
            track.hide();
            this.shownPlaceholderTracks.delete(uid);
        }
    }
    emitActiveVideoTracks() {
        this.repairStalePlaceholderTracksForRemoteVideo();
        const remoteTracks = this.optimalDelayMapper.getActiveVideoTracks();
        const tracks = remoteTracks.map((t) => ({
            ...t,
            isLocal: false,
        }));
        this.lastEmittedVideoTrackCount = remoteTracks.length;
        if (this.localUserId) {
            if (this.currentVideoTrack) {
                const localTrackId = this.currentVideoTrack.id;
                if (!tracks.find((t) => t.trackId === localTrackId)) {
                    tracks.unshift({
                        trackId: localTrackId,
                        userId: this.localUserId,
                        isLocal: true,
                    });
                }
            }
            for (const [trackId] of this.additionalVideoTracks) {
                if (!tracks.find((t) => t.trackId === trackId)) {
                    tracks.push({
                        trackId,
                        userId: this.localUserId,
                        isLocal: true,
                    });
                }
            }
        }
        const localCanonical = this.localUserId
            ? canonicalUserId(this.localUserId)
            : undefined;
        for (const userId of this.shownPlaceholderTracks) {
            tracks.push({
                trackId: `placeholder:${userId}`,
                userId,
                isLocal: localCanonical !== undefined && userId === localCanonical,
            });
        }
        for (const [trackId] of this.ografTracks) {
            tracks.push({
                trackId,
                userId: trackId,
                isLocal: true,
            });
        }
        this.emit('activeVideoTracksChanged', tracks);
    }
    setLocalUserId(userId, displayName) {
        const uid = canonicalUserId(userId);
        this.localUserId = uid;
        this.createPlaceholderTrack(uid, displayName ?? uid, true);
    }
    getLocalUserId() {
        return this.localUserId;
    }
    emitActiveAudioTracks() {
        const rawTracks = this.optimalDelayMapper.getActiveAudioTracks();
        this.lastEmittedAudioTrackCount = rawTracks.length;
        const tracks = rawTracks.map((t) => {
            const state = this.audioTrackStates.get(t.trackId);
            return {
                trackId: t.trackId,
                userId: t.userId,
                muted: state?.muted ?? false,
                gain: state?.gain ?? 100,
                isLocal: false,
            };
        });
        const localUserId = this.localUserId;
        if (localUserId) {
            const addLocalAudioTrack = (trackId) => {
                if (!tracks.find((t) => t.trackId === trackId)) {
                    const state = this.audioTrackStates.get(trackId);
                    tracks.push({
                        trackId,
                        userId: localUserId,
                        muted: state?.muted ?? false,
                        gain: state?.gain ?? 100,
                        isLocal: true,
                    });
                }
            };
            if (this.currentAudioTrack) {
                addLocalAudioTrack(this.currentAudioTrack.id);
            }
            for (const [trackId] of this.additionalAudioTracks) {
                addLocalAudioTrack(trackId);
            }
        }
        this.emit('activeAudioTracksChanged', tracks);
    }
    setAudioTrackMuted(trackId, muted) {
        const state = this.audioTrackStates.get(trackId) ?? {
            muted: false,
            gain: 100,
        };
        state.muted = muted;
        this.audioTrackStates.set(trackId, state);
        if (muted) {
            this.audioEngine.setTrackGain(trackId, 0);
        }
        else {
            this.audioEngine.setTrackGain(trackId, state.gain / 100);
        }
        this.emitActiveAudioTracks();
    }
    setAudioTrackGain(trackId, gain) {
        const state = this.audioTrackStates.get(trackId) ?? {
            muted: false,
            gain: 100,
        };
        state.gain = gain;
        this.audioTrackStates.set(trackId, state);
        this.audioEngine.setTrackGain(trackId, gain / 100);
        this.emitActiveAudioTracks();
    }
    resolveSourceIdToTrackId(sourceId) {
        if (this.localUserId && sourceId === this.localUserId) {
            if (this.currentVideoTrack) {
                return this.currentVideoTrack.id;
            }
            const firstAdditional = this.additionalVideoTracks.keys().next();
            if (!firstAdditional.done) {
                return firstAdditional.value;
            }
        }
        // Check if sourceId is a local video trackId (primary or additional)
        if (this.currentVideoTrack && this.currentVideoTrack.id === sourceId) {
            return sourceId;
        }
        if (this.additionalVideoTracks.has(sourceId)) {
            return sourceId;
        }
        try {
            const trackIds = this.optimalDelayMapper.getVideoTrackIdsForUser(sourceId);
            if (trackIds.length > 0) {
                return trackIds[0];
            }
        }
        catch {
            // sourceId is not a valid user ID; fall through to trackId-based lookup
        }
        if (this.optimalDelayMapper.getTrackType(sourceId) === 'video') {
            return sourceId;
        }
        if (this.ografTracks.has(sourceId)) {
            return sourceId;
        }
        return undefined;
    }
    resolveBackground(background) {
        if (!background || background.type !== 'track')
            return background;
        const resolved = this.resolveSourceIdToTrackId(background.trackId);
        if (!resolved)
            return undefined;
        return { type: 'track', trackId: resolved };
    }
    async setDisplayScene(config) {
        const resolved = this.resolveSceneConfig(config);
        await this.videoEngine.setDisplayScene(resolved);
    }
    async setLiveScene(config) {
        const resolved = this.resolveSceneConfig(config);
        await this.videoEngine.setLiveScene(resolved);
    }
    resolveSceneConfig(config) {
        let resolvedSlots;
        if (config.layoutId === 'manual' && config.videoSlots) {
            resolvedSlots = config.videoSlots.map((pos) => {
                if (pos.imageBitmap) {
                    return {
                        x: pos.x,
                        y: pos.y,
                        width: pos.width,
                        height: pos.height,
                        zIndex: pos.zIndex,
                        imageBitmap: pos.imageBitmap,
                    };
                }
                const trackId = pos.sourceId
                    ? this.resolveSourceIdToTrackId(pos.sourceId)
                    : this.currentVideoTrack?.id;
                return {
                    x: pos.x,
                    y: pos.y,
                    width: pos.width,
                    height: pos.height,
                    zIndex: pos.zIndex,
                    trackId,
                };
            });
        }
        const id = config.id;
        if (!id) {
            console.warn('MediaManager::resolveSceneConfig called with empty/nullish id');
        }
        return {
            id: id || 'default',
            layoutId: config.layoutId,
            videoSlots: resolvedSlots,
            background: this.resolveBackground(config.background),
        };
    }
    async setExcludeLocalTracksFromMainCanvas(exclude) {
        await this.videoEngine.setExcludeLocalTracksFromMainCanvas(exclude);
    }
    lowEndFocusUserId = null;
    setLowEndFocusUserId(userId) {
        this.lowEndFocusUserId = userId;
        this.resolveLowEndFocusTrack();
    }
    resolveLowEndFocusTrack() {
        if (!this.lowEndFocusUserId) {
            this.videoEngine.setFocusRemoteTrackId(null);
            return;
        }
        const trackIds = this.optimalDelayMapper.getVideoTrackIdsForUser(this.lowEndFocusUserId);
        this.videoEngine.setFocusRemoteTrackId(trackIds.length > 0 ? trackIds[0] : null);
    }
    async setThumbnailRenderingConfig(entries) {
        this.pendingThumbnailEntries = entries.map((e) => ({
            canvasId: e.canvasId,
            trackId: e.trackId,
        }));
        await this.resolveThumbnailConfig(entries);
    }
    resolveTrackIdForThumbnail(userId) {
        if (this.localUserId && userId === this.localUserId) {
            if (this.currentVideoTrack) {
                return this.currentVideoTrack.id;
            }
            if (this.shownPlaceholderTracks.has(userId)) {
                return `placeholder:${userId}`;
            }
        }
        try {
            const videoTrackIds = this.optimalDelayMapper.getVideoTrackIdsForUser(userId);
            if (videoTrackIds.length > 0) {
                return videoTrackIds[0];
            }
        }
        catch {
            // userId is not a valid user ID (e.g. composite-viewer-*);
            // fall through to trackId-based lookup
        }
        if (this.optimalDelayMapper.getTrackType(userId) === 'video') {
            return userId;
        }
        return userId;
    }
    async resolveThumbnailConfig(entries) {
        const resolved = entries.map((entry) => ({
            canvasId: entry.canvasId,
            trackId: this.resolveTrackIdForThumbnail(entry.trackId),
            canvas: entry.canvas,
        }));
        await this.videoEngine.setThumbnailRenderingConfig(resolved);
    }
    async resyncThumbnailConfig() {
        if (this.pendingThumbnailEntries.length === 0)
            return;
        await this.resolveThumbnailConfig(this.pendingThumbnailEntries);
    }
}
